import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["id", "isRequired", "isInvalid", "isDisabled", "isReadOnly"],
  _excluded2 = ["getRootProps", "htmlProps"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
// src/form-control.tsx
import { createContext } from "@chakra-ui/react-context";
import { mergeRefs } from "@chakra-ui/react-use-merge-refs";
import { chakra, forwardRef, omitThemingProps, useMultiStyleConfig } from "@chakra-ui/system";
import { cx, dataAttr } from "@chakra-ui/shared-utils";
import { useCallback, useId, useState } from "react";
import { jsx } from "react/jsx-runtime";
var _createContext = createContext({
    name: "FormControlStylesContext",
    errorMessage: "useFormControlStyles returned is 'undefined'. Seems you forgot to wrap the components in \"<FormControl />\" "
  }),
  _createContext2 = _slicedToArray(_createContext, 2),
  FormControlStylesProvider = _createContext2[0],
  useFormControlStyles = _createContext2[1];
var _createContext3 = createContext({
    strict: false,
    name: "FormControlContext"
  }),
  _createContext4 = _slicedToArray(_createContext3, 2),
  FormControlProvider = _createContext4[0],
  useFormControlContext = _createContext4[1];
function useFormControlProvider(props) {
  var idProp = props.id,
    isRequired = props.isRequired,
    isInvalid = props.isInvalid,
    isDisabled = props.isDisabled,
    isReadOnly = props.isReadOnly,
    htmlProps = _objectWithoutProperties(props, _excluded);
  var uuid = useId();
  var id = idProp || "field-".concat(uuid);
  var labelId = "".concat(id, "-label");
  var feedbackId = "".concat(id, "-feedback");
  var helpTextId = "".concat(id, "-helptext");
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasFeedbackText = _useState2[0],
    setHasFeedbackText = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hasHelpText = _useState4[0],
    setHasHelpText = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isFocused = _useState6[0],
    setFocus = _useState6[1];
  var getHelpTextProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({
      id: helpTextId
    }, props2), {}, {
      ref: mergeRefs(forwardedRef, function (node) {
        if (!node) return;
        setHasHelpText(true);
      })
    });
  }, [helpTextId]);
  var getLabelProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props2), {}, {
      ref: forwardedRef,
      "data-focus": dataAttr(isFocused),
      "data-disabled": dataAttr(isDisabled),
      "data-invalid": dataAttr(isInvalid),
      "data-readonly": dataAttr(isReadOnly),
      id: props2.id !== void 0 ? props2.id : labelId,
      htmlFor: props2.htmlFor !== void 0 ? props2.htmlFor : id
    });
  }, [id, isDisabled, isFocused, isInvalid, isReadOnly, labelId]);
  var getErrorMessageProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({
      id: feedbackId
    }, props2), {}, {
      ref: mergeRefs(forwardedRef, function (node) {
        if (!node) return;
        setHasFeedbackText(true);
      }),
      "aria-live": "polite"
    });
  }, [feedbackId]);
  var getRootProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread(_objectSpread({}, props2), htmlProps), {}, {
      ref: forwardedRef,
      role: "group"
    });
  }, [htmlProps]);
  var getRequiredIndicatorProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props2), {}, {
      ref: forwardedRef,
      role: "presentation",
      "aria-hidden": true,
      children: props2.children || "*"
    });
  }, []);
  return {
    isRequired: !!isRequired,
    isInvalid: !!isInvalid,
    isReadOnly: !!isReadOnly,
    isDisabled: !!isDisabled,
    isFocused: !!isFocused,
    onFocus: function onFocus() {
      return setFocus(true);
    },
    onBlur: function onBlur() {
      return setFocus(false);
    },
    hasFeedbackText: hasFeedbackText,
    setHasFeedbackText: setHasFeedbackText,
    hasHelpText: hasHelpText,
    setHasHelpText: setHasHelpText,
    id: id,
    labelId: labelId,
    feedbackId: feedbackId,
    helpTextId: helpTextId,
    htmlProps: htmlProps,
    getHelpTextProps: getHelpTextProps,
    getErrorMessageProps: getErrorMessageProps,
    getRootProps: getRootProps,
    getLabelProps: getLabelProps,
    getRequiredIndicatorProps: getRequiredIndicatorProps
  };
}
var FormControl = forwardRef(function FormControl2(props, ref) {
  var styles = useMultiStyleConfig("Form", props);
  var ownProps = omitThemingProps(props);
  var _useFormControlProvid = useFormControlProvider(ownProps),
    getRootProps = _useFormControlProvid.getRootProps,
    _ = _useFormControlProvid.htmlProps,
    context = _objectWithoutProperties(_useFormControlProvid, _excluded2);
  var className = cx("chakra-form-control", props.className);
  return /* @__PURE__ */jsx(FormControlProvider, {
    value: context,
    children: /* @__PURE__ */jsx(FormControlStylesProvider, {
      value: styles,
      children: /* @__PURE__ */jsx(chakra.div, _objectSpread(_objectSpread({}, getRootProps({}, ref)), {}, {
        className: className,
        __css: styles["container"]
      }))
    })
  });
});
FormControl.displayName = "FormControl";
var FormHelperText = forwardRef(function FormHelperText2(props, ref) {
  var field = useFormControlContext();
  var styles = useFormControlStyles();
  var className = cx("chakra-form__helper-text", props.className);
  return /* @__PURE__ */jsx(chakra.div, _objectSpread(_objectSpread({}, field == null ? void 0 : field.getHelpTextProps(props, ref)), {}, {
    __css: styles.helperText,
    className: className
  }));
});
FormHelperText.displayName = "FormHelperText";
export { useFormControlStyles, useFormControlContext, FormControl, FormHelperText };